/**
 * Site-wide auth modal + dropdown wiring.
 *
 * Triggers:
 *   - .big-squash-open-login → opens login modal
 *   - .big-squash-logout-button → POST /auth/logout, reload
 *   - .big-squash-user-dropdown__trigger → toggles dropdown
 *   - [data-big-squash-login] / [data-big-squash-modal-login] → submit login
 *   - [data-big-squash-forgot] / [data-big-squash-forgot-form] → forgot password flow
 *   - [data-big-squash-resend-form] → resend verification email (login page)
 *   - [data-big-squash-reset-form] → reset password submit
 */

import { __ } from '../lib/i18n.js';

const cfg = () => window.bigSquashAuth || {};

function api(path, opts = {}) {
    const { restUrl, restNonce } = cfg();
    return fetch(`${restUrl}${path}`, {
        credentials: 'same-origin',
        ...opts,
        headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': restNonce,
            ...(opts.headers || {}),
        },
    });
}

async function readJson(res) {
    try {
        return await res.json();
    } catch {
        return null;
    }
}

function esc(text) {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
    return String(text == null ? '' : text).replace(/[&<>"']/g, (m) => map[m]);
}

function showError(form, message) {
    const errEl = form.querySelector('[data-error]');
    if (errEl) {
        errEl.textContent = message;
        errEl.hidden = false;
    }
}

function clearError(form) {
    const errEl = form.querySelector('[data-error]');
    if (errEl) {
        errEl.textContent = '';
        errEl.hidden = true;
    }
}

/**
 * Ask the server for a fresh verification link and show the outcome in
 * statusEl. The server answers the same way whether or not the address is
 * known, so the message is deliberately non-committal.
 */
async function resendVerification(email, statusEl) {
    if (statusEl) {
        statusEl.textContent = __('Sending…');
        statusEl.hidden = false;
    }
    const res = await api('/auth/resend-verification', { method: 'POST', body: JSON.stringify({ email }) });
    const body = await readJson(res);
    if (!statusEl) return res.ok;
    if (res.ok) {
        statusEl.textContent = (body && body.message) || __('If that address has an unverified account, a new link is on its way.');
    } else {
        statusEl.textContent = (body && body.message) || __('Could not send the email. Please try again later.');
    }
    return res.ok;
}

/**
 * Wire a "resend" trigger that sits inside a login form: reveal it when the
 * login failed because the account is unverified, and send to the email the
 * user just typed.
 */
function bindInlineResend(form, wrapEl, buttonEl, statusEl) {
    if (!wrapEl || !buttonEl) return null;
    buttonEl.addEventListener('click', async () => {
        const email = (form.querySelector('[name="email"]') || {}).value || '';
        if (!email) return;
        buttonEl.disabled = true;
        await resendVerification(email, statusEl);
        buttonEl.disabled = false;
    });
    return {
        show() { wrapEl.hidden = false; },
        hide() {
            wrapEl.hidden = true;
            if (statusEl) statusEl.hidden = true;
        },
    };
}

/** The modal markup; mirrors src/Presentation/Frontend/View/auth-modal.php. */
function modalTemplate() {
    return `
<div class="big-squash-modal" data-big-squash-modal hidden>
    <div class="big-squash-modal__backdrop" data-modal-close></div>
    <div class="big-squash-modal__panel" role="dialog" aria-modal="true">
        <button type="button" class="big-squash-modal__close" data-modal-close aria-label="${esc(__('Close'))}">×</button>
        <h2>${esc(__('Log in'))}</h2>
        <form data-big-squash-modal-login>
            <label><span>${esc(__('Email'))}</span><input type="email" name="email" required autocomplete="email" /></label>
            <label><span>${esc(__('Password'))}</span><input type="password" name="password" required autocomplete="current-password" /></label>
            <label class="big-squash-checkbox"><input type="checkbox" name="remember" /> <span>${esc(__('Remember me'))}</span></label>
            <div class="big-squash-form-error" data-error hidden></div>
            <p class="big-squash-resend-inline" data-modal-resend hidden>
                ${esc(__("Didn't get the verification email?"))}
                <button type="button" class="big-squash-btn-link" data-modal-resend-btn>${esc(__('Send it again'))}</button>
                <span class="big-squash-auth-notice" data-modal-resend-status hidden></span>
            </p>
            <button type="submit" class="big-squash-btn big-squash-btn--primary">${esc(__('Log in'))}</button>
        </form>
        <p class="big-squash-auth-links">
            <a data-modal-register-link href="#">${esc(__('Create an account'))}</a>
            &nbsp;·&nbsp;
            <a data-modal-forgot href="#">${esc(__('Forgot password?'))}</a>
        </p>
        <div class="big-squash-forgot-form" data-modal-forgot-form hidden>
            <p>${esc(__("Enter your email and we'll send you a reset link."))}</p>
            <form data-modal-forgot-submit>
                <input type="email" name="email" required placeholder="${esc(__('you@example.com'))}" />
                <button type="submit" class="big-squash-btn">${esc(__('Send link'))}</button>
            </form>
            <p class="big-squash-auth-notice" data-forgot-status hidden></p>
        </div>
    </div>
</div>`;
}

let modalEl = null;
let modalResend = null;

function ensureModal() {
    if (modalEl) return modalEl;
    const wrap = document.createElement('div');
    wrap.innerHTML = modalTemplate().trim();
    modalEl = wrap.firstChild;
    document.body.appendChild(modalEl);

    const registerLink = modalEl.querySelector('[data-modal-register-link]');
    if (registerLink) registerLink.href = cfg().registerUrl || '#';

    modalEl.querySelectorAll('[data-modal-close]').forEach((el) => {
        el.addEventListener('click', closeModal);
    });

    const loginForm = modalEl.querySelector('[data-big-squash-modal-login]');
    loginForm.addEventListener('submit', handleModalLogin);
    modalResend = bindInlineResend(
        loginForm,
        modalEl.querySelector('[data-modal-resend]'),
        modalEl.querySelector('[data-modal-resend-btn]'),
        modalEl.querySelector('[data-modal-resend-status]')
    );

    const forgotToggle = modalEl.querySelector('[data-modal-forgot]');
    if (forgotToggle) {
        forgotToggle.addEventListener('click', (e) => {
            e.preventDefault();
            const wrap = modalEl.querySelector('[data-modal-forgot-form]');
            if (wrap) wrap.hidden = !wrap.hidden;
        });
    }

    const forgotSubmit = modalEl.querySelector('[data-modal-forgot-submit]');
    if (forgotSubmit) forgotSubmit.addEventListener('submit', handleForgotPassword);

    return modalEl;
}

function openModal() {
    const m = ensureModal();
    m.hidden = false;
    document.documentElement.classList.add('big-squash-modal-open');
    const firstField = m.querySelector('input[name="email"]');
    if (firstField) firstField.focus();
}

function closeModal() {
    if (!modalEl) return;
    modalEl.hidden = true;
    document.documentElement.classList.remove('big-squash-modal-open');
}

async function handleModalLogin(e) {
    e.preventDefault();
    const form = e.currentTarget;
    clearError(form);
    if (modalResend) modalResend.hide();
    const data = Object.fromEntries(new FormData(form).entries());
    data.remember = form.querySelector('[name="remember"]').checked;
    const res = await api('/auth/login', { method: 'POST', body: JSON.stringify(data) });
    const body = await readJson(res);
    if (!res.ok) {
        showError(form, (body && body.message) || __('Login failed'));
        if (body && body.code === 'unverified' && modalResend) modalResend.show();
        return;
    }
    window.location.reload();
}

async function handleForgotPassword(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());
    const status = modalEl.querySelector('[data-forgot-status]');
    await api('/auth/forgot-password', { method: 'POST', body: JSON.stringify(data) });
    if (status) {
        status.textContent = __("If that email is registered, you'll receive a reset link shortly.");
        status.hidden = false;
    }
}

async function doLogout() {
    await api('/auth/logout', { method: 'POST' });
    window.location.reload();
}

function bindLoginPageForm() {
    const form = document.querySelector('[data-big-squash-login]');
    if (!form) return;

    // Resend box below the form: opened by PHP after a dead verification link,
    // or here when login fails because the account is still unverified.
    const resendWrap = document.querySelector('[data-big-squash-resend-form]');
    const resendForm = resendWrap ? resendWrap.querySelector('form') : null;
    const resendStatus = resendWrap ? resendWrap.querySelector('[data-resend-status]') : null;
    if (resendForm) {
        resendForm.addEventListener('submit', async (e) => {
            e.preventDefault();
            const email = (resendForm.querySelector('[name="email"]') || {}).value || '';
            const button = resendForm.querySelector('button');
            if (button) button.disabled = true;
            await resendVerification(email, resendStatus);
            if (button) button.disabled = false;
        });
    }

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        clearError(form);
        const data = Object.fromEntries(new FormData(form).entries());
        data.remember = form.querySelector('[name="remember"]').checked;
        const res = await api('/auth/login', { method: 'POST', body: JSON.stringify(data) });
        const body = await readJson(res);
        if (!res.ok) {
            showError(form, (body && body.message) || __('Login failed'));
            if (body && body.code === 'unverified' && resendWrap) {
                resendWrap.hidden = false;
                const emailField = resendForm ? resendForm.querySelector('[name="email"]') : null;
                if (emailField && !emailField.value) emailField.value = data.email || '';
            }
            return;
        }
        const profileUrl = cfg().profileUrl || '/';
        window.location.href = profileUrl;
    });

    const forgotLink = document.querySelector('[data-big-squash-forgot]');
    const forgotWrap = document.querySelector('[data-big-squash-forgot-form]');
    if (forgotLink && forgotWrap) {
        forgotLink.addEventListener('click', (e) => {
            e.preventDefault();
            forgotWrap.hidden = !forgotWrap.hidden;
        });
        const innerForm = forgotWrap.querySelector('form');
        const status = forgotWrap.querySelector('[data-forgot-status]');
        if (innerForm) {
            innerForm.addEventListener('submit', async (e) => {
                e.preventDefault();
                const data = Object.fromEntries(new FormData(innerForm).entries());
                await api('/auth/forgot-password', { method: 'POST', body: JSON.stringify(data) });
                if (status) {
                    status.textContent = __("If that email is registered, you'll receive a reset link shortly.");
                    status.hidden = false;
                }
            });
        }
    }
}

function bindResetForm() {
    const form = document.querySelector('[data-big-squash-reset-form]');
    if (!form) return;
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        clearError(form);
        const data = Object.fromEntries(new FormData(form).entries());
        const res = await api('/auth/reset-password', { method: 'POST', body: JSON.stringify(data) });
        const body = await readJson(res);
        if (!res.ok) {
            showError(form, (body && body.message) || __('Reset failed'));
            return;
        }
        window.location.href = cfg().loginUrl || '/login';
    });
}

function bindGlobalListeners() {
    document.addEventListener('click', (e) => {
        const loginTrigger = e.target.closest('.big-squash-open-login');
        if (loginTrigger) {
            e.preventDefault();
            openModal();
            return;
        }
        const logoutBtn = e.target.closest('.big-squash-logout-button');
        if (logoutBtn) {
            e.preventDefault();
            doLogout();
            return;
        }
        const dropdownTrigger = e.target.closest('.big-squash-user-dropdown__trigger');
        if (dropdownTrigger) {
            e.preventDefault();
            const dropdown = dropdownTrigger.closest('.big-squash-user-dropdown');
            if (dropdown) {
                const expanded = dropdown.classList.toggle('is-open');
                dropdownTrigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
            }
            return;
        }
        // Click outside dropdown closes it
        document.querySelectorAll('.big-squash-user-dropdown.is-open').forEach((dd) => {
            if (!dd.contains(e.target)) {
                dd.classList.remove('is-open');
                const trig = dd.querySelector('.big-squash-user-dropdown__trigger');
                if (trig) trig.setAttribute('aria-expanded', 'false');
            }
        });
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            closeModal();
            document.querySelectorAll('.big-squash-user-dropdown.is-open').forEach((dd) => {
                dd.classList.remove('is-open');
                const trig = dd.querySelector('.big-squash-user-dropdown__trigger');
                if (trig) trig.setAttribute('aria-expanded', 'false');
            });
            return;
        }
        // The trigger and the logout item are <a> elements with button/menuitem
        // roles: Enter clicks them natively, Space has to be mapped by hand.
        if (e.key === ' ' && e.target instanceof Element) {
            const actionable = e.target.closest('.big-squash-user-dropdown__trigger, .big-squash-logout-button');
            if (actionable) {
                e.preventDefault();
                actionable.click();
            }
        }
    });
}

/**
 * The dropdown trigger and its panel are rendered as plain siblings inside the
 * theme's menu <li> (or the shortcode wrapper) so the theme's own menu-link rules
 * reach the trigger. Mark that parent as the container the panel is anchored to
 * and toggled on.
 */
function tagDropdownContainers() {
    document.querySelectorAll('.big-squash-user-dropdown__trigger').forEach((trigger) => {
        if (trigger.parentElement) {
            trigger.parentElement.classList.add('big-squash-user-dropdown');
        }
    });
}

function init() {
    tagDropdownContainers();
    bindGlobalListeners();
    bindLoginPageForm();
    bindResetForm();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
